import { logger } from '@/lib/logger';
import { getStoredToken } from '@/lib/googleAuth';

/**
 * Google Drive Meeting Tracker Service
 * 
 * Architecture: Root Folder → Rep Subfolders → Files (videos, transcripts, docs)
 * Each subfolder name = Sales Rep name.
 * Files are classified as: video (meeting recordings), transcript, document, or other.
 */

// --- File Classification Helpers ---

const VIDEO_MIMES = [
    'video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo',
    'video/x-matroska', 'video/avi'
];

const TRANSCRIPT_KEYWORDS = [
    'transcript', 'meeting notes', 'call notes', 'minutes', 'summary',
    'notes by gemini', 'notes by google', 'meeting started'
];

const DOC_MIMES = [
    'application/vnd.google-apps.document',
    'application/vnd.google-apps.spreadsheet',
    'application/vnd.google-apps.presentation',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

/**
 * Classifies a single Drive file into a category
 * @param {Object} file - Google Drive file object
 * @returns {'video'|'transcript'|'document'|'folder'|'other'}
 */
const classifyFile = (file) => {
    if (file.mimeType === 'application/vnd.google-apps.folder') return 'folder';

    const nameLower = (file.name || '').toLowerCase();
    const fileSizeMB = file.size ? parseInt(file.size) / (1024 * 1024) : 0;

    // Video detection (Google Meet recordings)
    // Google Meet recordings: codes like "xgq-riai-hzz (2026-04-17 22:51 GMT+5:30)" or .mp4 / .webm
    if (
        file.mimeType?.startsWith('video/') ||
        VIDEO_MIMES.includes(file.mimeType) || 
        /\.(mp4|webm|mov|mkv|avi|m4v)$/i.test(file.name || '') ||
        nameLower.includes('recording') ||
        nameLower.includes('google meet') ||
        (fileSizeMB > 8 && (/\d{4}-\d{2}-\d{2}/.test(file.name) || /^[a-z]{3}-[a-z]{4}-[a-z]{3}/.test(nameLower)))
    ) {
        return 'video';
    }

    // Transcript detection (by name heuristic — matches actual Drive & Google Meet conventions)
    if (
        TRANSCRIPT_KEYWORDS.some(kw => nameLower.includes(kw)) ||
        nameLower.includes('transcript') ||
        nameLower.includes('meeting notes') ||
        nameLower.includes('gemini') ||
        (file.mimeType === 'application/vnd.google-apps.document' && 
            (nameLower.includes('meet') || nameLower.includes('discussion') || /^[a-z]{3}-[a-z]{4}-[a-z]{3}/.test(nameLower)))
    ) {
        return 'transcript';
    }

    // Document detection
    if (
        DOC_MIMES.includes(file.mimeType) || 
        /\.(docx|xlsx|pptx|pdf|txt|csv)$/i.test(file.name || '')
    ) {
        return 'document';
    }

    return 'other';
};

/**
 * Extracts a date from a file — uses modifiedTime
 * @param {Object} file
 * @returns {Date}
 */
const getFileDate = (file) => new Date(file.modifiedTime);

/**
 * Returns YYYY-MM key for monthly grouping
 * @param {Date} date
 * @returns {string}
 */
const toMonthKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

// --- Core API Helpers ---

const DRIVE_API = 'https://www.googleapis.com/drive/v3/files';
const FILE_FIELDS = 'files(id, name, mimeType, size, modifiedTime, createdTime, iconLink, webViewLink, owners(displayName, photoLink))';

/**
 * Makes an authenticated request to Google Drive API
 * @param {string} url
 * @returns {Promise<Object>}
 */
const driveRequest = async (url) => {
    const token = getStoredToken();
    if (!token?.access_token) {
        throw new Error('Google OAuth token missing. Please connect your account.');
    }

    const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token.access_token}` }
    });

    if (!response.ok) {
        if (response.status === 401) {
            sessionStorage.removeItem('google_oauth_token');
            throw new Error('Google OAuth token expired. Please click "Connect Google Drive" to reconnect.');
        }
        const error = await response.json().catch(() => ({}));
        logger.error('Drive API Error:', error);
        throw new Error(`Drive API error: ${response.status} - ${error.error?.message || 'Unknown'}`);
    }

    return response.json();
};

/**
 * Makes an authenticated request to Google Drive API and returns text content
 * @param {string} url
 * @returns {Promise<string>}
 */
const driveRequestText = async (url) => {
    const token = getStoredToken();
    if (!token?.access_token) {
        throw new Error('Google OAuth token missing. Please connect your account.');
    }

    const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token.access_token}` }
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        logger.error('Drive API Error:', error);
        throw new Error(`Drive API error: ${response.status} - ${error.error?.message || 'Unknown'}`);
    }

    return response.text();
};

// --- Main Service ---

export const GoogleDriveService = {
    /**
     * Downloads text content of a Google Doc or plain text file.
     * @param {Object} file
     * @returns {Promise<string|null>}
     */
    fetchDocumentContent: async (file) => {
        try {
            if (file.mimeType === 'application/vnd.google-apps.document') {
                // Export Google Doc as plain text
                const url = `${DRIVE_API}/${file.id}/export?mimeType=text/plain`;
                return await driveRequestText(url);
            } else if (file.mimeType.includes('text/plain') || file.mimeType === 'application/rtf') {
                // Get media directly
                const url = `${DRIVE_API}/${file.id}?alt=media`;
                return await driveRequestText(url);
            }
            return null; // Unsupported type for parsing
        } catch (err) {
            logger.error(`Failed to fetch content for file ${file.id}`, err);
            return null;
        }
    },

    /**
     * Parses the transcript content and checks against compliance parameters.
     * @param {string} text
     * @returns {Object}
     */
    parseTranscript: (text) => {
        if (!text || text.trim() === '') {
            return { parsed: false, status: 'empty', checks: {} };
        }

        const lowerText = text.toLowerCase();
        
        // Define compliance parameters (Regex / Keyword based check)
        const checks = {
            pricingMentioned: lowerText.includes('price') || lowerText.includes('cost') || lowerText.includes('$') || lowerText.includes('pricing') || lowerText.includes('budget'),
            nextStepsDefined: lowerText.includes('next step') || lowerText.includes('action item') || lowerText.includes('follow up') || lowerText.includes('moving forward'),
            decisionMaker: lowerText.includes('decision maker') || lowerText.includes('approve') || lowerText.includes('sign off') || lowerText.includes('stakeholder')
        };

        const passedChecks = Object.values(checks).filter(Boolean).length;
        const totalChecks = Object.keys(checks).length;
        const score = (passedChecks / totalChecks) * 100;

        let status = 'failed';
        if (score === 100) status = 'passed';
        else if (score >= 33) status = 'warning';

        return {
            parsed: true,
            status,
            score,
            checks
        };
    },

    /**
     * Extracts folder ID from a variety of Google Drive URL formats
     * @param {string} url - The Drive folder URL or raw ID
     * @returns {string|null}
     */
    extractFolderId: (url) => {
        if (!url) return null;
        
        // Pattern for /folders/ID
        const folderMatch = url.match(/\/folders\/([a-zA-Z0-9-_]+)/);
        if (folderMatch && folderMatch[1]) return folderMatch[1];
        
        // Pattern for ?id=ID
        const idMatch = url.match(/[?&]id=([a-zA-Z0-9-_]+)/);
        if (idMatch && idMatch[1]) return idMatch[1];
        
        // If the string itself looks like an ID
        if (/^[a-zA-Z0-9-_]{15,}$/.test(url)) return url;
        
        return null;
    },

    /**
     * Fetches top-level subfolders inside a root folder from Google Drive API.
     * Each subfolder = one Sales Rep (or channel).
     * @param {string} rootFolderId
     * @returns {Promise<Array<{id: string, name: string, webViewLink?: string}>>}
     */
    fetchRepFolders: async (rootFolderId) => {
        const token = getStoredToken();
        if (!token?.access_token) {
            throw new Error('Google OAuth token missing. Please click "Connect Google Drive" to scan.');
        }

        // Real Google Drive Query: Find all subfolders in the rootFolderId
        const q = encodeURIComponent(`'${rootFolderId}' in parents and mimeType = 'application/vnd.google-apps.folder' and trashed = false`);
        const url = `${DRIVE_API}?q=${q}&fields=files(id, name, modifiedTime, createdTime, webViewLink)&pageSize=1000&orderBy=name`;

        const data = await driveRequest(url);
        const folders = data.files || [];

        if (folders.length > 0) {
            return folders;
        }

        // If no subfolders were found, check if this folder itself contains video/recording files directly
        const filesQ = encodeURIComponent(`'${rootFolderId}' in parents and trashed = false`);
        const filesUrl = `${DRIVE_API}?q=${filesQ}&fields=files(id, name)&pageSize=10`;
        const filesData = await driveRequest(filesUrl);
        if (filesData.files && filesData.files.length > 0) {
            return [{
                id: rootFolderId,
                name: 'All Meetings / Main Vault',
                lastModified: new Date().toISOString(),
                webViewLink: `https://drive.google.com/drive/folders/${rootFolderId}`
            }];
        }

        return [];
    },

    /**
     * Fetches contents of a single folder level
     * @param {string} folderId 
     * @returns {Promise<Array>}
     */
    fetchFolderLevel: async (folderId) => {
        const q = encodeURIComponent(`'${folderId}' in parents and trashed = false`);
        const url = `${DRIVE_API}?q=${q}&fields=${FILE_FIELDS}&pageSize=1000&orderBy=folder,name`;
        
        const data = await driveRequest(url);
        return (data.files || []).map(file => ({
            ...file,
            category: classifyFile(file),
            fileDate: getFileDate(file),
            monthKey: toMonthKey(getFileDate(file)),
            owner: file.owners?.[0]?.displayName || 'Unknown',
            ownerPhoto: file.owners?.[0]?.photoLink || null,
            sizeFormatted: file.size ? `${(parseInt(file.size) / (1024 * 1024)).toFixed(1)} MB` : '—'
        }));
    },

    /**
     * Fetches all files inside a rep's folder RECURSIVELY (2 levels deep).
     * This handles structures like: Rep Folder → Meet Recordings → actual files
     * @param {string} repFolderId
     * @param {Object} dateRange - Optional { start: Date, end: Date }
     * @param {number} depth - Recursion depth limit (default 3)
     * @returns {Promise<Array>}
     */
    fetchRepFiles: async (repFolderId, dateRange = null, depth = 3) => {
        const fetchLevel = async (folderId, currentDepth) => {
            if (currentDepth <= 0) return [];

            // Fetch everything (files + subfolders) in this folder
            let qParts = [`'${folderId}' in parents`, `trashed = false`];
            const q = encodeURIComponent(qParts.join(' and '));
            
            let items = [];
            let pageToken = null;
            
            do {
                const tokenParam = pageToken ? `&pageToken=${pageToken}` : '';
                const url = `${DRIVE_API}?q=${q}&fields=${FILE_FIELDS}&pageSize=1000&orderBy=modifiedTime desc${tokenParam}`;
                
                const data = await driveRequest(url);
                if (data.files) {
                    items = items.concat(data.files);
                }
                pageToken = data.nextPageToken;
            } while (pageToken);

            // Separate files from subfolders
            const files = [];
            const subfolders = [];

            for (const item of items) {
                if (item.mimeType === 'application/vnd.google-apps.folder') {
                    subfolders.push(item);
                } else {
                    // Apply date filter on files
                    const fileDate = new Date(item.modifiedTime);
                    if (dateRange?.start && fileDate < dateRange.start) continue;
                    if (dateRange?.end && fileDate > dateRange.end) continue;
                    files.push(item);
                }
            }

            // Recurse into subfolders (e.g., "Meet Recordings")
            const subResults = await Promise.all(
                subfolders.map(folder => fetchLevel(folder.id, currentDepth - 1))
            );

            return [...files, ...subResults.flat()];
        };

        const rawFiles = await fetchLevel(repFolderId, depth);

        // Deduplicate by ID and classify
        const seen = new Set();
        return rawFiles
            .filter(f => {
                if (seen.has(f.id)) return false;
                seen.add(f.id);
                return true;
            })
            .map(file => ({
                ...file,
                category: classifyFile(file),
                fileDate: getFileDate(file),
                monthKey: toMonthKey(getFileDate(file)),
                owner: file.owners?.[0]?.displayName || 'Unknown',
                ownerPhoto: file.owners?.[0]?.photoLink || null,
                sizeFormatted: file.size ? `${(parseInt(file.size) / (1024 * 1024)).toFixed(1)} MB` : '—'
            }));
    },

    /**
     * Builds a detailed report for a single Sales Rep
     * @param {string} repName
     * @param {Array} files - Classified file array from fetchRepFiles
     * @returns {Object}
     */
    buildRepReport: (repName, files) => {
        const videos = files.filter(f => f.category === 'video');
        const transcripts = files.filter(f => f.category === 'transcript');
        const documents = files.filter(f => f.category === 'document');

        // Transcript-to-meeting matching: ±1 day proximity by modifiedTime
        const matchedTranscripts = new Set();
        const unmatchedVideos = [];

        videos.forEach(video => {
            const videoDate = video.fileDate.getTime();
            const match = transcripts.find(t => {
                if (matchedTranscripts.has(t.id)) return false;
                const diff = Math.abs(t.fileDate.getTime() - videoDate);
                return diff <= 24 * 60 * 60 * 1000; // ±1 day
            });
            if (match) {
                matchedTranscripts.add(match.id);
            } else {
                unmatchedVideos.push(video);
            }
        });

        // Docs-without-meeting: documents that have no video within ±2 days
        const docsWithoutMeeting = documents.filter(doc => {
            const docDate = doc.fileDate.getTime();
            return !videos.some(v => {
                const diff = Math.abs(v.fileDate.getTime() - docDate);
                return diff <= 2 * 24 * 60 * 60 * 1000; // ±2 days
            });
        });

        // Monthly breakdown (by file modifiedTime)
        const meetingsByMonth = {};
        videos.forEach(v => {
            const key = v.monthKey;
            meetingsByMonth[key] = (meetingsByMonth[key] || 0) + 1;
        });

        // Current and previous month keys
        const now = new Date();
        const currentMonthKey = toMonthKey(now);
        const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const prevMonthKey = toMonthKey(prevMonth);

        const meetingsThisMonth = meetingsByMonth[currentMonthKey] || 0;
        const meetingsLastMonth = meetingsByMonth[prevMonthKey] || 0;

        const transcriptCoverage = videos.length > 0 
            ? Math.round((matchedTranscripts.size / videos.length) * 100) 
            : 100; // No meetings = 100% coverage

        // Last activity across all files
        const lastActivity = files.length > 0 
            ? new Date(Math.max(...files.map(f => f.fileDate.getTime())))
            : null;

        return {
            repName,
            totalFiles: files.length,
            totalMeetings: videos.length,
            totalTranscripts: transcripts.length,
            totalDocuments: documents.length,
            transcriptCoverage,
            docsWithoutMeeting: docsWithoutMeeting.length,
            docsWithoutMeetingList: docsWithoutMeeting,
            meetingsThisMonth,
            meetingsLastMonth,
            meetingTrend: meetingsThisMonth - meetingsLastMonth,
            meetingsByMonth,
            unmatchedVideos,
            lastActivity,
            files, // Keep raw files for expandable list
            // Status derivation
            status: transcriptCoverage >= 80 ? 'healthy' : transcriptCoverage >= 50 ? 'warning' : 'critical',
        };
    },

    /**
     * Full orchestrator: fetches rep folders → files per rep → builds full dashboard report
     * @param {string} rootFolderId
     * @param {Object} dateRange - Optional { start: Date, end: Date }
     * @returns {Promise<Object>}
     */
    buildFullReport: async (rootFolderId, dateRange = null) => {
        // Step 1: Get all rep folders
        const repFolders = await GoogleDriveService.fetchRepFolders(rootFolderId);

        if (repFolders.length === 0) {
            return {
                reps: [],
                totals: { reps: 0, meetings: 0, transcripts: 0, docsOnly: 0, coverage: 100 },
                lastScan: new Date().toISOString()
            };
        }

        // Step 2: Fetch files per rep in parallel (batched for API limits)
        const BATCH_SIZE = 5;
        const repReports = [];

        for (let i = 0; i < repFolders.length; i += BATCH_SIZE) {
            const batch = repFolders.slice(i, i + BATCH_SIZE);
            const batchResults = await Promise.all(
                batch.map(async (folder) => {
                    try {
                        const files = await GoogleDriveService.fetchRepFiles(folder.id, dateRange);
                        const repReport = GoogleDriveService.buildRepReport(folder.name, files);
                        return {
                            ...repReport,
                            folderId: folder.id,
                            folderLink: folder.webViewLink || `https://drive.google.com/drive/folders/${folder.id}`
                        };
                    } catch (err) {
                        logger.error(`Failed to fetch files for rep: ${folder.name}`, err);
                        return {
                            repName: folder.name,
                            folderId: folder.id,
                            folderLink: folder.webViewLink || `https://drive.google.com/drive/folders/${folder.id}`,
                            totalFiles: 0,
                            totalMeetings: 0,
                            totalTranscripts: 0,
                            totalDocuments: 0,
                            transcriptCoverage: 0,
                            docsWithoutMeeting: 0,
                            docsWithoutMeetingList: [],
                            meetingsThisMonth: 0,
                            meetingsLastMonth: 0,
                            meetingTrend: 0,
                            meetingsByMonth: {},
                            unmatchedVideos: [],
                            lastActivity: null,
                            files: [],
                            status: 'error',
                            error: err.message
                        };
                    }
                })
            );
            repReports.push(...batchResults);
        }

        // Step 3: Aggregate totals
        const totals = {
            reps: repReports.length,
            meetings: repReports.reduce((sum, r) => sum + r.totalMeetings, 0),
            meetingsThisMonth: repReports.reduce((sum, r) => sum + r.meetingsThisMonth, 0),
            transcripts: repReports.reduce((sum, r) => sum + r.totalTranscripts, 0),
            docsOnly: repReports.reduce((sum, r) => sum + r.docsWithoutMeeting, 0),
            coverage: repReports.length > 0
                ? Math.round(repReports.reduce((sum, r) => sum + r.transcriptCoverage, 0) / repReports.length)
                : 100,
            healthy: repReports.filter(r => r.status === 'healthy').length,
            warning: repReports.filter(r => r.status === 'warning').length,
            critical: repReports.filter(r => r.status === 'critical').length,
        };

        return {
            reps: repReports.sort((a, b) => a.repName.localeCompare(b.repName)),
            totals,
            lastScan: new Date().toISOString()
        };
    },

    // ---- Legacy Compat (keep for any existing references) ----

    /**
     * @deprecated Use buildFullReport instead
     */
    fetchFolderContents: async (folderId, depth = 4) => {
        const token = getStoredToken();
        if (!token?.access_token) {
            throw new Error('Google OAuth token missing. Please connect your account.');
        }

        const fetchPage = async (currentFolderId, currentDepth) => {
            if (currentDepth <= 0) return [];

            try {
                const fields = 'files(id, name, mimeType, size, modifiedTime, iconLink, webViewLink, owners(displayName, photoLink))';
                const q = `'${currentFolderId}' in parents and trashed = false`;
                
                const response = await fetch(
                    `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&fields=${fields}&pageSize=1000`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token.access_token}`
                        }
                    }
                );

                if (!response.ok) {
                    const error = await response.json();
                    logger.error('Drive API Error:', error);
                    return [];
                }

                const data = await response.json();
                const files = data.files || [];
                
                const subFolders = files.filter(f => f.mimeType === 'application/vnd.google-apps.folder');
                
                if (currentDepth === 1 || subFolders.length === 0) {
                    return files;
                }

                const subFolderPromises = subFolders.map(folder => fetchPage(folder.id, currentDepth - 1));
                const subFolderResults = await Promise.all(subFolderPromises);
                
                return [...files, ...subFolderResults.flat()];
            } catch (error) {
                logger.error('Error in fetchPage:', error);
                return [];
            }
        };

        return await fetchPage(folderId, depth);
    },

    /**
     * @deprecated Use buildRepReport instead
     */
    categorizeFiles: (files, reviewCycleMonths = 6) => {
        const report = {
            total: files.length,
            folders: 0, mp4: 0, docs: 0, others: 0,
            compliant: 0, dueSoon: 0, overdue: 0,
            items: []
        };

        const now = new Date();
        const cycleMs = reviewCycleMonths * 30 * 24 * 60 * 60 * 1000;

        report.items = files.map(file => {
            const isFolder = file.mimeType === 'application/vnd.google-apps.folder';
            const isMp4 = file.mimeType === 'video/mp4' || file.name.endsWith('.mp4');
            const isDoc = file.mimeType.includes('document') || 
                         file.mimeType.includes('pdf') || 
                         file.mimeType.includes('spreadsheet') ||
                         file.name.endsWith('.docx') ||
                         file.name.endsWith('.xlsx') ||
                         file.name.endsWith('.pdf');

            if (isFolder) report.folders++;
            else if (isMp4) report.mp4++;
            else if (isDoc) report.docs++;
            else report.others++;

            let type = 'other';
            if (isFolder) type = 'folder';
            else if (isMp4) type = 'video';
            else if (isDoc) type = 'document';

            let status = 'compliant';
            let reviewDueDate = null;
            
            if (!isFolder) {
                const modifiedAt = new Date(file.modifiedTime);
                reviewDueDate = new Date(modifiedAt.getTime() + cycleMs);
                
                const msUntilDue = reviewDueDate.getTime() - now.getTime();
                if (msUntilDue < 0) {
                    status = 'overdue';
                    report.overdue++;
                } else if (msUntilDue < 30 * 24 * 60 * 60 * 1000) {
                    status = 'dueSoon';
                    report.dueSoon++;
                } else {
                    status = 'compliant';
                    report.compliant++;
                }
            }

            return {
                id: file.id,
                name: file.name,
                type, mimeType: file.mimeType,
                size: file.size ? (parseInt(file.size) / (1024 * 1024)).toFixed(2) + ' MB' : '--',
                modifiedAt: file.modifiedTime,
                reviewDueDate: reviewDueDate?.toISOString(),
                status, icon: file.iconLink, url: file.webViewLink,
                owner: file.owners?.[0]?.displayName || 'Unknown'
            };
        });

        return report;
    }
};
